import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { FournisseurSession, useSession } from "./auth/Session";
import { Connexion } from "./ecrans/Connexion";
import { ListeDossiers } from "./ecrans/ListeDossiers";
import { FicheClient } from "./ecrans/FicheClient";
import { PortailClient } from "./portail/PortailClient";
import { ConnexionClient } from "./portail/ConnexionClient";
import { CreationCompte } from "./portail/CreationCompte";
import { Recrutement } from "./recrutement/Recrutement";
import { Configuration } from "./ecrans/Configuration";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

/** Les rôles servis par le portail client, par opposition à l'équipe MDC. */
const ROLES_PORTAIL = ["client", "delegue"];

/**
 * Fermé par défaut côté front aussi : aucune route métier hors session.
 *
 * ⛔ LE RENVOI DEPEND DU ROLE ATTENDU.
 * Renvoyer un client sur l'écran de l'équipe lui présenterait un formulaire où
 * ses identifiants ne fonctionnent pas : il conclurait que son compte est cassé.
 */
function Protege({ roles, children }: { roles: string[]; children: React.ReactNode }) {
  const { session } = useSession();
  const ecranDeConnexion = roles.some((r) => ROLES_PORTAIL.includes(r))
    ? "/portail/connexion"
    : "/connexion";

  if (!session) return <Navigate to={ecranDeConnexion} replace />;
  if (!roles.includes(session.role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function Aiguillage() {
  const { session } = useSession();
  if (!session) return <Navigate to="/connexion" replace />;
  return <Navigate to={ROLES_PORTAIL.includes(session.role) ? "/portail" : "/dossiers"} replace />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <FournisseurSession>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Aiguillage />} />
            <Route path="/connexion" element={<Connexion />} />

            {/* §5.A — Le lien d'invitation N'OUVRE PLUS DE SESSION. Il ne sert
                qu'une fois, a etablir des identifiants ; ensuite, connexion. */}
            <Route path="/depot/:jeton" element={<CreationCompte />} />
            <Route path="/portail/connexion" element={<ConnexionClient />} />

            {/* §5.N — le SEUL espace ouvert a Internet. */}
            <Route path="/recrutement" element={<Recrutement />} />

            <Route
              path="/dossiers"
              element={
                <Protege roles={["staff", "admin"]}>
                  <ListeDossiers />
                </Protege>
              }
            />
            {/* Reservee au GERANT : ces reglages valent pour tous les clients. */}
            <Route
              path="/configuration"
              element={
                <Protege roles={["admin"]}>
                  <Configuration />
                </Protege>
              }
            />

            <Route
              path="/dossiers/:dossierId/:ecran?"
              element={
                <Protege roles={["staff", "admin"]}>
                  <FicheClient />
                </Protege>
              }
            />
            {/* Le portail d'un client, vu par un collaborateur MDC.
                Declare AVANT « /portail/:onglet » — sinon « apercu » serait lu
                comme un nom d'onglet. Le classement de React Router fait deja
                gagner le segment statique, mais l'ordre le rend evident.

                ⛔ Aucune session client n'est fabriquee : le collaborateur entre
                avec SON jeton. Le serveur l'autorise parce qu'il est staff
                (`AccesAutorise`), et toute action porte SA signature. */}
            <Route
              path="/portail/apercu/:dossierId/:onglet?"
              element={
                <Protege roles={["staff", "admin"]}>
                  <PortailClient />
                </Protege>
              }
            />

            <Route
              path="/portail/:onglet?"
              element={
                <Protege roles={["client", "delegue"]}>
                  <PortailClient />
                </Protege>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </FournisseurSession>
    </QueryClientProvider>
  );
}
